
import React from 'react';
import * as Icons from 'lucide-react';

export type SkillSlideType = 'overview' | 'supplies' | 'procedure' | 'criticals' | 'reporting' | 'completion';

export interface SkillItemSlide {
  id: string;
  type: SkillSlideType;
  title?: string;
  data: any;
}

export interface SkillSlide {
  id: string;
  name: string;
  icon: string;
  slides: SkillItemSlide[];
}

export const SkillIconResolver = (name: string): React.FC<any> => {
  // @ts-ignore
  return Icons[name] || Icons.Activity;
};

export const CNA_SKILLS: SkillSlide[] = [
  {
    id: 'hand-washing',
    name: 'Hand Hygiene (Hand Washing)',
    icon: 'Hand',
    hasSimulator: false,
    isSimulatorEnabled: false,
    simulatorScenarioId: '',
    hasVideo: true,
    videoUrl: 'https://www.youtube.com/watch?v=3PmVJQUCm4E',
    procedureStepsCount: 3,
    hasCriticals: true,
    slides: [
      { id: 's1', type: 'overview', data: { importance: 'Primary defense against cross-contamination and nosocomial infections.', examContext: 'Tested as part of EVERY other skill.', safetyFocus: 'Infection Control, Barrier Protection.' } },
      { id: 's2', type: 'supplies', data: { items: ['Soap', 'Paper Towels', 'Waste Container', 'Running Water'] } },
      { id: 's-video', type: 'embedded', title: 'Clinical Demonstration Video', data: { embedMode: 'url', embedUrl: 'https://www.youtube.com/watch?v=3PmVJQUCm4E', videoTitle: 'WHO: How to Handwash? Hand Hygiene Clinical Technique', videoAuthor: 'World Health Organization (WHO)', aspectRatio: '16:9' } },
      { id: 's3', type: 'procedure', title: 'Phase 1: Preparation & Water Flow', data: { steps: ['Turn on water.', 'Wet hands/wrists (fingertips down).', 'Apply soap.'] } },
      { id: 's4', type: 'procedure', title: 'Phase 2: Lather & Friction', data: { steps: ['Lather surfaces for 20 seconds.', 'Clean under fingernails.', 'Keep hands lower than elbows.'] } },
      { id: 's5', type: 'procedure', title: 'Phase 3: Rinse & Barrier Technique', data: { steps: ['Rinse thoroughly.', 'Dry with clean towels.', 'Turn off faucet with dry barrier towel.'] } },
      { id: 's6', type: 'criticals', data: { items: ['Lather for full 20s.', 'Do not touch inside of sink.', 'Use barrier towel for faucet.'] } },
      { id: 's7', type: 'completion', data: { items: ['Hands clean/dry.', 'Faucet off with barrier.', 'Towels disposed.'] } }
    ]
  },
  {
    id: 'radial-pulse',
    name: 'Radial Pulse Measurement',
    icon: 'HeartPulse',
    hasSimulator: false,
    isSimulatorEnabled: false,
    simulatorScenarioId: '',
    hasVideo: false,
    procedureStepsCount: 1,
    hasCriticals: true,
    slides: [
      { id: 'rp1', type: 'overview', data: { importance: 'Monitoring cardiovascular health and identifying tachycardia or bradycardia.', examContext: 'Requires 100% accuracy within +/- 4 beats of the examiner.', safetyFocus: 'Clinical Accuracy and Professional Touch.' } },
      { id: 'rp2', type: 'supplies', data: { items: ['Watch with Second Hand', 'Recording Form', 'Pen'] } },
      { id: 'rp3', type: 'procedure', title: 'Positioning', data: { steps: ['Locate radial artery on thumb side of wrist.', 'Place tips of two or three fingers over artery.', 'Support resident arm comfortably.'] } },
      { id: 'rp4', type: 'criticals', data: { items: ['Count for full 60 seconds.', 'Do not use thumb (it has its own pulse).', 'Inform resident you are beginning pulse count.'] } },
      { id: 'rp5', type: 'reporting', data: { instruction: 'Record rate per minute on documentation sheet.', sampleWording: 'Nurse, I have recorded Mrs. Smith’s radial pulse at 72 beats per minute.' } },
      { id: 'rp6', type: 'completion', data: { items: ['Pulse recorded.', 'Resident comfortable.', 'Call light in reach.'] } }
    ]
  },
  {
    id: 'ambulation-gait-belt',
    name: 'Ambulation with Gait Belt',
    icon: 'Accessibility',
    hasSimulator: false,
    isSimulatorEnabled: false,
    simulatorScenarioId: '',
    hasVideo: true,
    videoUrl: 'https://www.youtube.com/watch?v=v0E5_iE8e5A',
    procedureStepsCount: 2,
    hasCriticals: true,
    slides: [
      { id: 'gb1', type: 'overview', data: { importance: 'Prevents resident falls while promoting mobility and independence.', examContext: 'High-risk skill; any balance failure is a mandatory fail.', safetyFocus: 'Fall Prevention & Proper Body Mechanics.' } },
      { id: 'gb2', type: 'supplies', data: { items: ['Gait Belt', 'Non-skid Shoes/Socks', 'Wheelchair (if part of transfer)'] } },
      { id: 'gb-video', type: 'embedded', title: 'Clinical Demonstration: Ambulation & Transfer', data: { embedMode: 'url', embedUrl: 'https://www.youtube.com/watch?v=v0E5_iE8e5A', videoTitle: 'Gait Belt Transfer and Ambulation Technique', videoAuthor: 'RegisteredNurseRN', aspectRatio: '16:9' } },
      { id: 'gb3', type: 'criticals', data: { items: ['Check brakes on wheelchair/bed.', 'Ensure resident has non-skid footwear.', 'Apply belt snugly over clothing.'] } },
      { id: 'gb4', type: 'procedure', title: 'The Transfer', data: { steps: ['Dangle resident on edge of bed.', 'Place belt around waist.', 'Assist to stand using proper grip.'] } },
      { id: 'gb5', type: 'procedure', title: 'Walking', data: { steps: ['Stand slightly behind and to the side.', 'Maintain firm grip on belt.', 'Walk 10 feet as instructed.'] } },
      { id: 'gb6', type: 'completion', data: { items: ['Resident seated safely.', 'Belt removed.', 'Equipment stored.'] } }
    ]
  }
];
